/**
 * Physics constants (world units; 1 block = 30 units, y grows upward).
 * These are Shape Shifters' own tuned values, chosen to feel genre-standard.
 * Any change here alters gameplay and must regenerate golden replay fixtures
 * (see docs/TEST_PLAN.md §5).
 */
export const UNITS_PER_BLOCK = 30;

/** Horizontal speed bands, units/second. */
export const SPEED = {
  half: 251.16,
  one: 311.58,
  two: 387.42,
  three: 468.0,
  four: 576.0,
} as const;
export type SpeedBand = keyof typeof SPEED;

/** Runner (ground auto-jump form). */
export const RUNNER = {
  gravity: 2795, // u/s², downward
  jumpVelocity: 603.72, // u/s  (apex ≈ 2.17 blocks, air time ≈ 0.432 s)
  terminalFall: -790, // u/s
  /** Full body hitbox (solids), units. */
  bodySize: 30,
  /** Reduced inner hitbox (hazards), units. */
  innerSize: 10,
  /**
   * When moving horizontally into a solid: if the solid's top is no more than
   * this far above the player's feet, the player snaps up (walks onto it);
   * any deeper frontal overlap is death.
   */
  stepTolerance: 9,
} as const;

/** Buffered-input window: a press this many seconds early still fires. */
export const INPUT_BUFFER_S = 0.1;

/** All player forms (original names; see FEATURE_MATRIX §2 for mapping). */
export type FormId =
  | 'runner' // ground auto-jump
  | 'glider' // hold-to-rise flyer
  | 'roller' // tap flips gravity while grounded
  | 'hopper' // tap = flap impulse
  | 'streak' // hold = diagonal up, dies on any solid
  | 'vaulter' // variable-height jump (hold time)
  | 'blinker' // tap = teleport to opposite surface + gravity flip
  | 'pendul'; // thrust toward gravity; tap flips gravity

export interface FormPhysics {
  /** Gravity magnitude while airborne, u/s². */
  gravity: number;
  /** Terminal speed along gravity axis, u/s. */
  terminal: number;
  /** Jump/flap impulse, u/s (0 = form has none). */
  jumpVelocity: number;
  /**
   * Vertical acceleration while held, replacing gravity for that tick.
   * Glider: strong positive (thrust up). Vaulter: small negative (reduced
   * gravity during the thrust window, giving variable jump height).
   */
  holdAccel: number;
  /** Rising into a ceiling face is fatal. */
  ceilingKills: boolean;
  /** Any solid contact is fatal (Streak). */
  solidKills: boolean;
}

export const FORM_PHYSICS: Record<FormId, FormPhysics> = {
  runner: {
    gravity: 2795,
    terminal: 790,
    jumpVelocity: 603.72,
    holdAccel: 0,
    ceilingKills: true,
    solidKills: false,
  },
  glider: {
    gravity: 1450,
    terminal: 460,
    jumpVelocity: 0,
    holdAccel: 3050,
    ceilingKills: false,
    solidKills: false,
  },
  roller: {
    gravity: 2795,
    terminal: 790,
    jumpVelocity: 0,
    holdAccel: 0,
    ceilingKills: false,
    solidKills: false,
  },
  hopper: {
    gravity: 2000,
    terminal: 640,
    jumpVelocity: 385,
    holdAccel: 0,
    ceilingKills: false,
    solidKills: false,
  },
  streak: {
    gravity: 0,
    terminal: 0,
    jumpVelocity: 0,
    holdAccel: 0,
    ceilingKills: false,
    solidKills: true,
  },
  vaulter: {
    gravity: 2795,
    terminal: 790,
    jumpVelocity: 360,
    holdAccel: -560,
    ceilingKills: true,
    solidKills: false,
  },
  blinker: {
    gravity: 2795,
    terminal: 900,
    jumpVelocity: 0,
    holdAccel: 0,
    ceilingKills: false,
    solidKills: false,
  },
  pendul: {
    gravity: 1800,
    terminal: 500,
    jumpVelocity: 0,
    holdAccel: 0,
    ceilingKills: false,
    solidKills: false,
  },
};

/** Vaulter thrust window: hold sustains lift for at most this many seconds. */
export const VAULTER_HOLD_S = 0.22;
/** Roller/pendul may flip within this window after leaving the surface. */
export const COYOTE_S = 0.02;
/** Mini-size scale factor applied to hitboxes; impulses scale by MINI_IMPULSE. */
export const MINI_SCALE = 0.6;
export const MINI_IMPULSE = 0.8;
/** Streak diagonal slope (vy = slope × vx); mini variant is steeper. */
export const STREAK_SLOPE = 1.0;
export const STREAK_SLOPE_MINI = 1.5;

/** Bounce-pad impulses, u/s (as a multiple of runner jump for feel parity). */
export const PAD_IMPULSE: Record<string, number> = {
  strong: 830, // high bounce
  soft: 500, // low bounce
  mega: 1000, // very high bounce
  flip: 620, // flips gravity and pushes along new gravity
};

/** Tap-orb impulses, u/s. */
export const ORB_IMPULSE: Record<string, number> = {
  strong: 603.72,
  soft: 420,
  mega: 850,
  flip: 500, // flips gravity
  'flip-boost': 620, // green: flip + boost
  slam: -700, // black: slams along gravity
  blink: 0, // spider-style teleport
  dash: 0, // handled as dash state
  gravdash: 0,
};

/** Fatal out-of-bounds band (units). Ground plane guards the bottom in normal mode. */
export const KILL_Y_MIN = -400;
export const KILL_Y_MAX = 4000;

/**
 * Extra tolerance when landing onto a 45°/22.5° slope surface, units.
 * (Platformer note: horizontal control is instant at the level's speed band,
 * and the Glider form doubles as the platformer jetpack — the "Booster" row
 * in the feature matrix — holding thrusts, exactly like the baseline's
 * platformer ship role.)
 */
export const SLOPE_LAND_TOL = 3;
